import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { useApp } from "../context/AppContext";

// ─── Shared: compute semantic N-grams from clusters ───
const STOP_WORDS_CLIENT = new Set([
  "та","і","в","у","на","з","із","за","до","від","по","для","як","що","це","той","ця","ті","але","бо",
  "ж","же","ні","не","так","чи","або","ще","вже","їх","його","її","він","вона","воно","вони","ми","ви",
  "при","про","через","між","під","над","без","щоб","коли","де","які","яка","який","яке","яких",
  "було","буде","був","була","були","є","бути","може","можна","треба","потрібно","також",
  "після","перед","свої","свій","все","всі","весь","кожен","кожна","лише","тільки",
  "и","в","на","с","по","за","из","от","до","для","не","но","да","то","же","бы","ли","как","что",
  "это","его","их","он","она","оно","они","мы","вы","все","при","про","без","под","над","между",
  "через","после","перед","если","был","была","было","были","быть","будет","может","можно",
  "the","a","an","is","are","was","were","be","been","being","have","has","had","do","does","did",
  "will","would","shall","should","may","might","can","could","must","to","of","in","for",
  "on","with","at","by","from","as","into","through","during","before","after","and","but","or","if",
]);

const computeSemNgrams = (clusters) => {
  if (!clusters.length) return null;

  const tokenize = (text) => {
    const words = text.toLowerCase().match(/[a-zа-яёіїєґ]+(?:['-][a-zа-яёіїєґ]+)*/gi) || [];
    return words.filter(w => !STOP_WORDS_CLIENT.has(w) && w.length >= 3);
  };

  const extractNgrams = (tokens, n) => {
    const grams = [];
    for (let i = 0; i <= tokens.length - n; i++) {
      const parts = tokens.slice(i, i + n);
      let hasDup = false;
      for (let j = 1; j < parts.length; j++) { if (parts[j] === parts[j - 1]) { hasDup = true; break; } }
      if (!hasDup) grams.push(parts.join(" "));
    }
    return grams;
  };

  const byUrlMap = new Map();
  let totalKw = 0;
  for (const c of clusters) {
    const url = c.url || "unknown";
    const kws = (c.keywords || []).map(k => {
      if (typeof k === "string") return { keyword: k, frequency: 1 };
      return { keyword: k.keyword || k.name || "", frequency: k.frequency || k.freq || 1 };
    }).filter(k => k.keyword);
    if (!kws.length) continue;
    if (!byUrlMap.has(url)) byUrlMap.set(url, []);
    const existing = new Set(byUrlMap.get(url).map(k => k.keyword.toLowerCase()));
    for (const kw of kws) {
      const lower = kw.keyword.toLowerCase();
      if (!existing.has(lower)) {
        existing.add(lower);
        byUrlMap.get(url).push(kw);
        totalKw++;
      }
    }
  }

  if (!totalKw) return null;

  const buildPageNgrams = (keywords) => {
    const gramData = { 1: new Map(), 2: new Map(), 3: new Map() };
    for (const kw of keywords) {
      const tokens = tokenize(kw.keyword);
      for (const n of [1, 2, 3]) {
        const grams = extractNgrams(tokens, n);
        const seen = new Set();
        for (const gram of grams) {
          if (seen.has(gram)) continue;
          seen.add(gram);
          if (!gramData[n].has(gram)) gramData[n].set(gram, { weight: 0, count: 0, sources: [] });
          const entry = gramData[n].get(gram);
          entry.weight += kw.frequency;
          entry.count++;
          entry.sources.push({ keyword: kw.keyword, frequency: kw.frequency });
        }
      }
    }
    return gramData;
  };

  const assignPriorities = (items) => {
    if (!items.length) return items;
    const sorted = [...items].sort((a, b) => b.weight - a.weight);
    const top20idx = Math.max(1, Math.ceil(sorted.length * 0.2));
    const top50idx = Math.max(1, Math.ceil(sorted.length * 0.5));
    const top20threshold = sorted[top20idx - 1]?.weight || 0;
    const top50threshold = sorted[top50idx - 1]?.weight || 0;
    return items.map(item => ({
      ...item,
      priority: (item.weight >= top20threshold || item.count >= 3) ? "high" : item.weight >= top50threshold ? "medium" : "low",
    }));
  };

  const toArray = (gramMap, limit = 500) =>
    Array.from(gramMap.entries())
      .map(([gram, d]) => ({ gram, weight: d.weight, count: d.count, sources: d.sources }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, limit);

  const byPage = new Map();
  for (const [url, keywords] of byUrlMap) {
    const gramData = buildPageNgrams(keywords);
    byPage.set(url, {
      unigrams: assignPriorities(toArray(gramData[1])),
      bigrams: assignPriorities(toArray(gramData[2])),
      trigrams: assignPriorities(toArray(gramData[3])),
    });
  }

  const globalGrams = { 1: new Map(), 2: new Map(), 3: new Map() };
  for (const [url, keywords] of byUrlMap) {
    for (const kw of keywords) {
      const tokens = tokenize(kw.keyword);
      for (const n of [1, 2, 3]) {
        const grams = extractNgrams(tokens, n);
        const seen = new Set();
        for (const gram of grams) {
          if (seen.has(gram)) continue;
          seen.add(gram);
          if (!globalGrams[n].has(gram)) globalGrams[n].set(gram, { weight: 0, count: 0, pages: new Set(), sources: [] });
          const entry = globalGrams[n].get(gram);
          entry.weight += kw.frequency;
          entry.count++;
          entry.pages.add(url);
          entry.sources.push({ keyword: kw.keyword, frequency: kw.frequency, url });
        }
      }
    }
  }

  const globalToArray = (gramMap, limit = 500) =>
    Array.from(gramMap.entries())
      .map(([gram, d]) => ({ gram, weight: d.weight, count: d.count, pages: d.pages.size, sources: d.sources }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, limit);

  return {
    global: {
      unigrams: globalToArray(globalGrams[1]),
      bigrams: globalToArray(globalGrams[2]),
      trigrams: globalToArray(globalGrams[3]),
    },
    byPage,
    totalKeywords: totalKw,
    totalPages: byUrlMap.size,
    pageUrls: [...byUrlMap.keys()],
  };
};

// ═══ NGRAM ANALYSIS VIEW ═══
export default function NgramView() {
  const { proj, activeId, getStore, apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td, bgTasks } = useApp();
  if (!proj) return null;
  const store = getStore(activeId);
  const clusters = store.clusters || [];
  const urls = [...new Set(clusters.map(c => c.url))];

  const [semViewMode, setSemViewMode] = useState("global");
  const [semSelectedUrl, setSemSelectedUrl] = useState("");

  const semNgrams = useMemo(() => computeSemNgrams(clusters), [clusters]);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, url: "" });
  const [tab, setTab] = useState("unigrams");
  const [minCount, setMinCount] = useState(2);
  const [search, setSearch] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [sortCol, setSortCol] = useState("weight");
  const [sortDir, setSortDir] = useState("desc");
  const [pg, setPg] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [expandedFull, setExpandedFull] = useState({});
  const PP = 50;

  // Load saved on mount
  useEffect(() => {
    apiFetch(`/api/projects/${activeId}/ngrams/saved`)
      .then(r => r.json())
      .then(d => {
        if (d && d.data) {
          setResults(d.data);
          setSavedAt(d.updated_at);
        }
      })
      .catch(() => {});
  }, [activeId]);

  // Auto-save after analysis
  const saveResults = useCallback(async (data) => {
    try {
      await apiFetch(`/api/projects/${activeId}/ngrams`, {
        method: "PUT",
        body: JSON.stringify({ data }),
      });
      setSavedAt(new Date().toISOString());
    } catch (e) {
      console.error("Save ngrams:", e);
    }
  }, [activeId, apiFetch]);

  // Track progress from bgTasks
  const activeTask = bgTasks.find(bt => bt.projectId === activeId && bt.type === "ngram_analysis" && bt.status === "running");
  const taskProgress = activeTask?.progress || {};

  // Auto-reload results when task completes
  useEffect(() => {
    const doneTask = bgTasks.find(bt => bt.projectId === activeId && bt.type === "ngram_analysis" && bt.status === "done");
    if (doneTask) {
      apiFetch(`/api/projects/${activeId}/ngrams/saved`)
        .then(r => r.json())
        .then(d => {
          if (d && d.data) {
            setResults(d.data);
            setSavedAt(d.updated_at);
          }
        })
        .catch(() => {});
      setLoading(false);
    }
  }, [bgTasks, activeId]);

  const runAnalysis = async () => {
    setLoading(true);
    setProgress({ current: 0, total: urls.length, url: "" });
    setPg(0);

    try {
      const resp = await apiFetch(`/api/projects/${activeId}/ngrams`, {
        method: "POST",
        body: JSON.stringify({ urls }),
      });
      const data = await resp.json();
      if (data.taskId) {
        flash("Задача N-грамм анализа запущена");
      }
    } catch (err) {
      console.error("Ngram analysis error:", err);
      setLoading(false);
    }
  };

  const isSemTab = tab.startsWith("sem_");
  const semTabKey = tab.replace("sem_", "");

  const getData = () => {
    if (isSemTab) {
      if (!semNgrams) return [];
      let items;
      if (semViewMode === "byPage" && semSelectedUrl) {
        const pageData = semNgrams.byPage.get(semSelectedUrl);
        items = pageData ? [...(pageData[semTabKey] || [])] : [];
      } else {
        items = [...(semNgrams.global[semTabKey] || [])];
      }
      if (minCount > 1) items = items.filter(i => i.count >= minCount);
      if (search) {
        const q = search.toLowerCase();
        items = items.filter(i => i.gram.includes(q));
      }
      items.sort((a, b) => {
        const mul = sortDir === "desc" ? -1 : 1;
        if (sortCol === "gram") return mul * a.gram.localeCompare(b.gram);
        if (sortCol === "priority") {
          const order = { high: 3, medium: 2, low: 1 };
          return mul * ((order[a.priority] || 0) - (order[b.priority] || 0));
        }
        return mul * ((a[sortCol] || 0) - (b[sortCol] || 0));
      });
      return items;
    }
    if (!results) return [];
    let items = [...(results[tab] || [])];
    if (minCount > 1) items = items.filter(i => i.count >= minCount);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(i => i.gram.includes(q));
    }
    items.sort((a, b) => {
      const mul = sortDir === "desc" ? -1 : 1;
      if (sortCol === "gram") return mul * a.gram.localeCompare(b.gram);
      return mul * ((a[sortCol] || 0) - (b[sortCol] || 0));
    });
    return items;
  };

  const data = getData();
  const totalPages = Math.ceil(data.length / PP);
  const pageData = data.slice(pg * PP, (pg + 1) * PP);

  const tabBtn = (key, label) => (
    <button
      key={key}
      onClick={() => { setTab(key); setPg(0); }}
      style={{
        ...btnG, fontWeight: tab === key ? 700 : 500,
        background: tab === key ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : t.bgCard,
        color: tab === key ? "#fff" : t.text,
        border: tab === key ? "none" : `1px solid ${t.border}`,
      }}
    >
      {label}
    </button>
  );

  const sortHeader = (col, label) => (
    <th
      onClick={() => { setSortCol(col); setSortDir(sortCol === col && sortDir === "desc" ? "asc" : "desc"); setPg(0); }}
      style={{ padding: "8px 12px", textAlign: "left", cursor: "pointer", fontSize: 12, fontWeight: 600, color: t.textMuted, borderBottom: `1px solid ${t.border}`, userSelect: "none" }}
    >
      {label} {sortCol === col ? (sortDir === "desc" ? "↓" : "↑") : ""}
    </th>
  );

  // ─── Competitor comparison state ───
  const [compDomains, setCompDomains] = useState(["", ""]);
  const [compResults, setCompResults] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compPhase, setCompPhase] = useState("");
  const [compProgress, setCompProgress] = useState({ current: 0, total: 0, url: "" });
  const [compCounts, setCompCounts] = useState(null);
  const [compTab, setCompTab] = useState("bigrams");
  const [compSearch, setCompSearch] = useState("");
  const [compOnlyGrowth, setCompOnlyGrowth] = useState(false);
  const [compSortCol, setCompSortCol] = useState("diff");
  const [compSortDir, setCompSortDir] = useState("desc");
  const [compPg, setCompPg] = useState(0);
  const [compSavedAt, setCompSavedAt] = useState(null);
  const compPP = 50;

  // Load saved compare on mount
  useEffect(() => {
    apiFetch(`/api/projects/${activeId}/ngrams/compare/saved`)
      .then(r => r.json())
      .then(d => {
        if (d && d.data) {
          setCompResults(d.data);
          setCompSavedAt(d.updated_at);
        }
      })
      .catch(() => {});
  }, [activeId]);

  const saveCompResults = useCallback(async (data) => {
    try {
      await apiFetch(`/api/projects/${activeId}/ngrams/compare`, {
        method: "PUT",
        body: JSON.stringify({ data }),
      });
      setCompSavedAt(new Date().toISOString());
    } catch (e) {
      console.error("Save compare:", e);
    }
  }, [activeId, apiFetch]);

  // Track compare progress from bgTasks
  const activeCompTask = bgTasks.find(bt => bt.projectId === activeId && bt.type === "ngram_compare" && bt.status === "running");
  const compTaskProgress = activeCompTask?.progress || {};

  // Auto-reload compare results when task completes
  useEffect(() => {
    const doneTask = bgTasks.find(bt => bt.projectId === activeId && bt.type === "ngram_compare" && bt.status === "done");
    if (doneTask) {
      apiFetch(`/api/projects/${activeId}/ngrams/compare/saved`)
        .then(r => r.json())
        .then(d => {
          if (d && d.data) {
            setCompResults(d.data);
            setCompSavedAt(d.updated_at);
          }
        })
        .catch(() => {});
      setCompLoading(false);
    }
  }, [bgTasks, activeId]);

  const runCompare = async () => {
    const domains = compDomains.filter(d => d.trim());
    if (!domains.length) return;
    const allKeywords = clusters.flatMap(c => (c.keywords || []).map(k => typeof k === "string" ? k : k.keyword || k.name || "")).filter(Boolean);
    setCompLoading(true);
    setCompPhase("sitemap");
    setCompProgress({ current: 0, total: 0, url: "" });
    setCompCounts(null);
    setCompPg(0);

    try {
      const resp = await apiFetch(`/api/projects/${activeId}/ngrams/compare`, {
        method: "POST",
        body: JSON.stringify({ competitors: domains, keywords: allKeywords, ourUrls: urls }),
      });
      const data = await resp.json();
      if (data.taskId) {
        flash("Задача сравнения запущена");
      }
    } catch (err) {
      console.error("Compare error:", err);
      setCompLoading(false);
    }
  };

  const getCompData = () => {
    if (!compResults || !results) return [];
    // Only OUR n-grams — look up their counts at competitors
    const ourItems = results[compTab] || [];

    const compMaps = {};
    Object.keys(compResults).forEach(domain => {
      const items = (compResults[domain] && compResults[domain][compTab]) || [];
      compMaps[domain] = {};
      items.forEach(i => { compMaps[domain][i.gram] = i.count; });
    });
    const domains = Object.keys(compMaps);

    let rows = ourItems.map(item => {
      const compValues = domains.map(d => compMaps[d][item.gram] || 0);
      const maxComp = Math.max(0, ...compValues);
      const diff = maxComp - item.count;
      const row = { gram: item.gram, ours: item.count, diff };
      domains.forEach(d => { row[d] = compMaps[d][item.gram] || 0; });
      return row;
    });

    if (compOnlyGrowth) rows = rows.filter(r => r.diff > 0);
    if (compSearch) {
      const q = compSearch.toLowerCase();
      rows = rows.filter(r => r.gram.includes(q));
    }

    rows.sort((a, b) => {
      const mul = compSortDir === "desc" ? -1 : 1;
      if (compSortCol === "gram") return mul * a.gram.localeCompare(b.gram);
      if (compSortCol === "ours" || compSortCol === "diff") return mul * (a[compSortCol] - b[compSortCol]);
      return mul * ((a[compSortCol] || 0) - (b[compSortCol] || 0));
    });

    return { rows, domains };
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>N-грамм анализ</h2>

      {/* Info */}
      <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <span style={{ fontSize: 13 }}>Страниц: <b>{urls.length}</b></span>
          {savedAt && <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 12 }}>Сохранено: {new Date(savedAt).toLocaleString()}</span>}
        </div>
        <button style={btnP} onClick={runAnalysis} disabled={loading || !!activeTask || !urls.length}>
          {(loading || activeTask) ? `Анализ... ${taskProgress.current || progress.current || 0}/${taskProgress.total || progress.total || urls.length}` : results ? "Переанализировать" : "Анализировать"}
        </button>
      </div>

      {/* Progress bar */}
      {(loading || activeTask) && (() => {
        const cur = taskProgress.current || progress.current || 0;
        const tot = taskProgress.total || progress.total || urls.length;
        const curUrl = taskProgress.url || progress.url || "";
        return (
          <div style={{ ...card, padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Прогресс: {cur}/{tot}</span>
              <span style={{ fontSize: 11, color: t.textMuted, maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{curUrl}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: t.border, overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 3, background: "linear-gradient(90deg, #6366f1, #a855f7)", width: `${tot ? (cur / tot) * 100 : 0}%`, transition: "width .3s" }} />
            </div>
          </div>
        );
      })()}

      {/* Results */}
      {(results || semNgrams) && (
        <div>
          <div style={{ ...card, padding: 12, marginBottom: 0, borderBottom: "none", borderRadius: "12px 12px 0 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {results && <>
                {tabBtn("unigrams", `1-граммы (${(results.unigrams || []).length})`)}
                {tabBtn("bigrams", `2-граммы (${(results.bigrams || []).length})`)}
                {tabBtn("trigrams", `3-граммы (${(results.trigrams || []).length})`)}
              </>}

              {semNgrams && <>
                {results && <span style={{ color: t.border, fontSize: 16, margin: "0 2px" }}>|</span>}
                {tabBtn("sem_unigrams", `СЯ 1-гр (${semNgrams.global.unigrams.length})`)}
                {tabBtn("sem_bigrams", `СЯ 2-гр (${semNgrams.global.bigrams.length})`)}
                {tabBtn("sem_trigrams", `СЯ 3-гр (${semNgrams.global.trigrams.length})`)}
              </>}

              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ fontSize: 11, color: t.textMuted }}>Мин:</label>
                <input
                  type="number" min={1} value={minCount}
                  onChange={e => { setMinCount(Math.max(1, parseInt(e.target.value) || 1)); setPg(0); }}
                  style={{ width: 50, padding: "4px 6px", borderRadius: 6, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text, fontSize: 12, fontFamily: "inherit" }}
                />
                <input
                  placeholder="Поиск..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPg(0); }}
                  style={{ width: 160, padding: "5px 10px", borderRadius: 6, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text, fontSize: 12, fontFamily: "inherit" }}
                />
              </div>
            </div>
            {isSemTab && semNgrams && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${t.border}` }}>
                  <button
                    onClick={() => { setSemViewMode("global"); setPg(0); }}
                    style={{ ...btnSm, borderRadius: 0, border: "none", background: semViewMode === "global" ? "#6366f1" : t.bgCard, color: semViewMode === "global" ? "#fff" : t.text, fontWeight: semViewMode === "global" ? 700 : 500 }}
                  >Все страницы</button>
                  <button
                    onClick={() => { setSemViewMode("byPage"); setPg(0); if (!semSelectedUrl && semNgrams.pageUrls.length) setSemSelectedUrl(semNgrams.pageUrls[0]); }}
                    style={{ ...btnSm, borderRadius: 0, border: "none", borderLeft: `1px solid ${t.border}`, background: semViewMode === "byPage" ? "#6366f1" : t.bgCard, color: semViewMode === "byPage" ? "#fff" : t.text, fontWeight: semViewMode === "byPage" ? 700 : 500 }}
                  >По страницам</button>
                </div>
                {semViewMode === "byPage" && (
                  <select
                    value={semSelectedUrl}
                    onChange={e => { setSemSelectedUrl(e.target.value); setPg(0); }}
                    style={{ flex: 1, minWidth: 200, maxWidth: 500, padding: "5px 8px", borderRadius: 8, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text, fontSize: 12, fontFamily: "inherit" }}
                  >
                    {semNgrams.pageUrls.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                )}
              </div>
            )}
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>
              Показано: {data.length} | {isSemTab ? `Ключевых слов: ${semNgrams?.totalKeywords || 0} | Страниц: ${semNgrams?.totalPages || 0}` : results ? `Успешно загружено: ${results.successUrls}/${results.totalUrls} страниц` : ""}
            </div>
          </div>

          {/* Table */}
          <div style={{ ...card, padding: 0, marginTop: 0, borderRadius: "0 0 12px 12px", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: dark ? "rgba(255,255,255,.03)" : "#fafafa" }}>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: t.textMuted, borderBottom: `1px solid ${t.border}`, width: 50 }}>#</th>
                  {sortHeader("gram", (tab === "unigrams" || tab === "sem_unigrams") ? "Слово" : "Фраза")}
                  {isSemTab ? sortHeader("weight", "Вес") : sortHeader("count", "Вхождений")}
                  {isSemTab ? sortHeader("count", "Ключей") : sortHeader("pages", "Страниц")}
                  {isSemTab && semViewMode === "global" && sortHeader("pages", "Страниц")}
                  {isSemTab && semViewMode === "byPage" && sortHeader("priority", "Приоритет")}
                </tr>
              </thead>
              <tbody>
                {pageData.map((item, i) => {
                  const isExp = expanded === item.gram;
                  const colCount = isSemTab ? (semViewMode === "global" ? 5 : 5) : 4;
                  const priorityColors = { high: { bg: "#22c55e", text: "#fff" }, medium: { bg: "#f59e0b", text: "#fff" }, low: { bg: dark ? "#444" : "#e5e7eb", text: t.textMuted } };
                  const priorityLabels = { high: "Высокий", medium: "Средний", low: "Низкий" };
                  return (
                    <Fragment key={item.gram}>
                      <tr
                        style={{ borderBottom: isExp ? "none" : `1px solid ${t.border}`, transition: "background .1s", cursor: "pointer" }}
                        onClick={() => setExpanded(isExp ? null : item.gram)}
                        onMouseEnter={e => e.currentTarget.style.background = t.hover}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: "7px 12px", fontSize: 12, color: t.textMuted }}>{pg * PP + i + 1}</td>
                        <td style={{ padding: "7px 12px", fontSize: 13, fontWeight: 500 }}>
                          <span style={{ marginRight: 6, fontSize: 10, color: t.textMuted }}>{isExp ? "▼" : "▶"}</span>
                          {item.gram}
                        </td>
                        <td style={{ padding: "7px 12px", fontSize: 13 }}>
                          <span style={{ background: "linear-gradient(135deg, #6366f1, #a855f7)", color: "#fff", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{isSemTab ? item.weight : item.count}</span>
                        </td>
                        <td style={{ padding: "7px 12px", fontSize: 12, color: t.textMuted }}>{isSemTab ? item.count : item.pages}</td>
                        {isSemTab && semViewMode === "global" && <td style={{ padding: "7px 12px", fontSize: 12, color: t.textMuted }}>{item.pages}</td>}
                        {isSemTab && semViewMode === "byPage" && (
                          <td style={{ padding: "7px 12px" }}>
                            <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 600, background: priorityColors[item.priority]?.bg, color: priorityColors[item.priority]?.text }}>{priorityLabels[item.priority] || item.priority}</span>
                          </td>
                        )}
                      </tr>
                      {isExp && isSemTab && item.sources && (
                        <tr>
                          <td colSpan={colCount} style={{ padding: 0, borderBottom: `1px solid ${t.border}` }}>
                            <div style={{ background: dark ? "rgba(255,255,255,.02)" : "#fafafa", padding: "8px 12px 8px 48px" }}>
                              {(() => {
                                const showAll = expandedFull[item.gram];
                                const visible = showAll ? item.sources : item.sources.slice(0, 10);
                                const hasMore = item.sources.length > 10;
                                return (
                                  <>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, marginBottom: 6 }}>
                                      "{item.gram}" (вес: {item.weight}) — Ключи ({item.sources.length}):
                                    </div>
                                    {visible.map((s, si) => (
                                      <div key={si} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 12 }}>
                                        <span style={{ background: dark ? "#2a2a3a" : "#e8e8f0", padding: "1px 7px", borderRadius: 8, fontSize: 11, fontWeight: 600, minWidth: 40, textAlign: "center" }}>{s.frequency}</span>
                                        <span>{s.keyword}</span>
                                        {s.url && <span style={{ color: t.textMuted, fontSize: 11 }}>— {s.url}</span>}
                                      </div>
                                    ))}
                                    {hasMore && (
                                      <button
                                        onClick={e => { e.stopPropagation(); setExpandedFull(prev => ({ ...prev, [item.gram]: !showAll })); }}
                                        style={{ ...btnSm, marginTop: 6, color: "#6366f1", border: `1px solid ${t.border}` }}
                                      >
                                        {showAll ? "Свернуть" : `Показать ещё ${item.sources.length - 10}`}
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                      {isExp && !isSemTab && (
                        <tr>
                          <td colSpan={4} style={{ padding: 0, borderBottom: `1px solid ${t.border}` }}>
                            <div style={{ background: dark ? "rgba(255,255,255,.02)" : "#fafafa", padding: "8px 12px 8px 48px" }}>
                              {item.urls && item.urls.length > 0 ? (() => {
                                const showAll = expandedFull[item.gram];
                                const visible = showAll ? item.urls : item.urls.slice(0, 5);
                                const hasMore = item.urls.length > 5;
                                return (
                                  <>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, marginBottom: 6 }}>Страницы ({item.urls.length}):</div>
                                    {visible.map(u => (
                                      <div key={u.url} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 12 }}>
                                        <span style={{ background: dark ? "#2a2a3a" : "#e8e8f0", padding: "1px 7px", borderRadius: 8, fontSize: 11, fontWeight: 600, minWidth: 30, textAlign: "center" }}>{u.count}</span>
                                        <a href={u.url} target="_blank" rel="noopener noreferrer" style={{ color: "#6366f1", textDecoration: "none", wordBreak: "break-all" }}
                                          onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                                          onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
                                        >{u.url}</a>
                                      </div>
                                    ))}
                                    {hasMore && (
                                      <button
                                        onClick={e => { e.stopPropagation(); setExpandedFull(prev => ({ ...prev, [item.gram]: !showAll })); }}
                                        style={{ ...btnSm, marginTop: 6, color: "#6366f1", border: `1px solid ${t.border}` }}
                                      >
                                        {showAll ? "Свернуть" : `Показать ещё ${item.urls.length - 5}`}
                                      </button>
                                    )}
                                  </>
                                );
                              })() : (
                                <div style={{ fontSize: 12, color: t.textMuted, padding: "4px 0" }}>Нет данных о страницах. Переанализируйте для получения детализации.</div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {pageData.length === 0 && (
                  <tr><td colSpan={isSemTab ? 6 : 4} style={{ padding: 30, textAlign: "center", color: t.textMuted, fontSize: 13 }}>Нет данных</td></tr>
                )}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ padding: "10px 18px", borderTop: `1px solid ${t.border}`, display: "flex", justifyContent: "center", gap: 6 }}>
                <button style={{ ...btnSm, opacity: pg === 0 ? 0.4 : 1 }} disabled={pg === 0} onClick={() => setPg(p => p - 1)}>←</button>
                <span style={{ fontSize: 12, color: t.textMuted, lineHeight: "28px" }}>{pg + 1} / {totalPages}</span>
                <button style={{ ...btnSm, opacity: pg >= totalPages - 1 ? 0.4 : 1 }} disabled={pg >= totalPages - 1} onClick={() => setPg(p => p + 1)}>→</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Competitor Comparison Section ─── */}
      <div style={{ marginTop: 28 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>Сравнение с конкурентами</h3>

        <div style={{ ...card, display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
          {compDomains.map((d, i) => (
            <div key={i} style={{ flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 4 }}>Sitemap конкурента {i + 1}</label>
              <input
                value={d}
                onChange={e => { const nd = [...compDomains]; nd[i] = e.target.value; setCompDomains(nd); }}
                placeholder="https://example.com/sitemap.xml"
                style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }}
              />
            </div>
          ))}
          <button style={btnP} onClick={runCompare} disabled={compLoading || !!activeCompTask || !compDomains.some(d => d.trim())}>
            {(compLoading || activeCompTask) ? `Анализ... ${compTaskProgress.current || 0}/${compTaskProgress.total || 0}` : "Сравнить с конкурентами"}
          </button>
        </div>

        {compSavedAt && !compLoading && (
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, marginBottom: 4 }}>
            Сохранено: {new Date(compSavedAt).toLocaleString()}
          </div>
        )}

        {/* Compare progress */}
        {compLoading && (
          <div style={{ ...card, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
              {compPhase === "sitemap" && "Парсинг sitemap конкурентов..."}
              {compPhase === "filter" && "Фильтрация URL по ключевым словам..."}
              {compPhase === "analyze" && `Анализ страниц: ${compProgress.current}/${compProgress.total}`}
            </div>
            {compCounts && (
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 6 }}>
                {Object.entries(compCounts).map(([d, c]) => (
                  <span key={d} style={{ marginRight: 14 }}>{d}: {c.filtered} подходящих / {c.total} всего URL</span>
                ))}
              </div>
            )}
            {compPhase === "analyze" && compProgress.total > 0 && (
              <div style={{ height: 6, borderRadius: 3, background: t.border, overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 3, background: "linear-gradient(90deg, #f59e0b, #ef4444)", width: `${(compProgress.current / compProgress.total) * 100}%`, transition: "width .3s" }} />
              </div>
            )}
          </div>
        )}

        {/* Compare results table */}
        {compResults && results && (() => {
          const { rows: compRows, domains } = getCompData();
          const compTotalPages = Math.ceil(compRows.length / compPP);
          const compPageData = compRows.slice(compPg * compPP, (compPg + 1) * compPP);

          const compTabBtn = (key, label) => (
            <button
              key={key}
              onClick={() => { setCompTab(key); setCompPg(0); }}
              style={{
                ...btnG, fontWeight: compTab === key ? 700 : 500,
                background: compTab === key ? "linear-gradient(135deg, #f59e0b, #ef4444)" : t.bgCard,
                color: compTab === key ? "#fff" : t.text,
                border: compTab === key ? "none" : `1px solid ${t.border}`,
              }}
            >
              {label}
            </button>
          );

          const compSortHeader = (col, label) => (
            <th
              onClick={() => { setCompSortCol(col); setCompSortDir(compSortCol === col && compSortDir === "desc" ? "asc" : "desc"); setCompPg(0); }}
              style={{ padding: "8px 10px", textAlign: "left", cursor: "pointer", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `1px solid ${t.border}`, userSelect: "none", whiteSpace: "nowrap" }}
            >
              {label} {compSortCol === col ? (compSortDir === "desc" ? "↓" : "↑") : ""}
            </th>
          );

          return (
            <div>
              <div style={{ ...card, padding: 12, marginBottom: 0, borderBottom: "none", borderRadius: "12px 12px 0 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {compTabBtn("bigrams", "2-граммы")}
                  {compTabBtn("trigrams", "3-граммы")}

                  <label style={{ marginLeft: 12, fontSize: 12, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                    <input type="checkbox" checked={compOnlyGrowth} onChange={e => { setCompOnlyGrowth(e.target.checked); setCompPg(0); }} />
                    Только точки роста
                  </label>

                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      placeholder="Поиск..."
                      value={compSearch}
                      onChange={e => { setCompSearch(e.target.value); setCompPg(0); }}
                      style={{ width: 160, padding: "5px 10px", borderRadius: 6, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text, fontSize: 12, fontFamily: "inherit" }}
                    />
                  </div>
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>
                  Показано: {compRows.length} фраз
                  {domains.map(d => {
                    const r = compResults[d];
                    return r ? <span key={d} style={{ marginLeft: 14 }}>{d}: {r.matchedUrls || 0} совпало / {r.totalUrls} всего</span> : null;
                  })}
                </div>
              </div>

              <div style={{ ...card, padding: 0, marginTop: 0, borderRadius: "0 0 12px 12px", overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
                  <thead>
                    <tr style={{ background: dark ? "rgba(255,255,255,.03)" : "#fafafa" }}>
                      <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `1px solid ${t.border}`, width: 40 }}>#</th>
                      {compSortHeader("gram", "Фраза")}
                      {compSortHeader("ours", "У нас")}
                      {domains.map(d => compSortHeader(d, d.length > 20 ? d.slice(0, 18) + "…" : d))}
                      {compSortHeader("diff", "Разница")}
                    </tr>
                  </thead>
                  <tbody>
                    {compPageData.map((row, i) => (
                      <tr key={row.gram} style={{ borderBottom: `1px solid ${t.border}` }}
                        onMouseEnter={e => e.currentTarget.style.background = t.hover}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: "6px 10px", fontSize: 11, color: t.textMuted }}>{compPg * compPP + i + 1}</td>
                        <td style={{ padding: "6px 10px", fontSize: 13, fontWeight: 500 }}>{row.gram}</td>
                        <td style={{ padding: "6px 10px", fontSize: 12 }}>
                          <span style={{ background: "linear-gradient(135deg, #6366f1, #a855f7)", color: "#fff", padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{row.ours}</span>
                        </td>
                        {domains.map(d => (
                          <td key={d} style={{ padding: "6px 10px", fontSize: 12 }}>
                            <span style={{ background: dark ? "#2a2a3a" : "#e8e8f0", padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{row[d] || 0}</span>
                          </td>
                        ))}
                        <td style={{ padding: "6px 10px", fontSize: 12 }}>
                          {row.diff > 0 ? (
                            <span style={{ background: "linear-gradient(135deg, #f59e0b, #ef4444)", color: "#fff", padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>+{row.diff}</span>
                          ) : row.diff === 0 ? (
                            <span style={{ color: t.textMuted, fontSize: 11 }}>0</span>
                          ) : (
                            <span style={{ background: "#10b981", color: "#fff", padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{row.diff}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {compPageData.length === 0 && (
                      <tr><td colSpan={4 + domains.length} style={{ padding: 30, textAlign: "center", color: t.textMuted, fontSize: 13 }}>Нет данных</td></tr>
                    )}
                  </tbody>
                </table>
                {compTotalPages > 1 && (
                  <div style={{ padding: "10px 18px", borderTop: `1px solid ${t.border}`, display: "flex", justifyContent: "center", gap: 6 }}>
                    <button style={{ ...btnSm, opacity: compPg === 0 ? 0.4 : 1 }} disabled={compPg === 0} onClick={() => setCompPg(p => p - 1)}>←</button>
                    <span style={{ fontSize: 12, color: t.textMuted, lineHeight: "28px" }}>{compPg + 1} / {compTotalPages}</span>
                    <button style={{ ...btnSm, opacity: compPg >= compTotalPages - 1 ? 0.4 : 1 }} disabled={compPg >= compTotalPages - 1} onClick={() => setCompPg(p => p + 1)}>→</button>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
